// src/lib/rich-text.ts
//
// Explanation text is AI-generated and arrives in Markdown: emphasis with
// **bold** / *italic*. The explanation render surfaces showed it as raw
// text, so students saw literal asterisks (Discord bug report 2026-06-13 —
// "too many asterisks").
//
// We deliberately do NOT pull a full Markdown engine over this content.
// Explanations are math-heavy, and a GFM parser would (a) read "x > 3" as a
// blockquote, "# of solutions" as a heading, etc., and (b) ignore the caret
// exponents (x^2) that aren't LaTeX-delimited. So we parse ONLY emphasis
// here and compose with the existing caret → superscript handling
// (src/lib/math-text.ts) at render time. Presentation-only; stored rows are
// untouched, and the whole class (old + future content) is fixed wherever
// <RichText> is used.
//
// Pure parser; the React renderer is src/components/RichText.tsx.

export type RichNode =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: RichNode[] }
  | { type: 'italic'; children: RichNode[] }

function isSpace(ch: string | undefined): boolean {
  return ch === undefined || ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r'
}

function isAlnum(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch)
}

/**
 * Find a flanking-valid closing delimiter of length `delim`, searching from
 * `from`. A closer must be immediately preceded by a non-space and (for a
 * single `*`) not immediately followed by an alphanumeric — so math like
 * `2*3` never closes a span. Returns the index of the closer's first `*`, or
 * -1 if there is none.
 */
function findCloser(input: string, from: number, delim: number): number {
  for (let k = from; k < input.length; k++) {
    if (input[k] !== '*') continue
    if (k === from) continue // empty span, e.g. "****"
    if (isSpace(input[k - 1])) continue
    if (delim === 2) {
      if (input[k + 1] !== '*') continue
    } else {
      // A single-`*` closer must not be part of a `**` run (those belong to a
      // bold delimiter), and must not be followed by an alphanumeric ("2*3").
      if (input[k - 1] === '*' || input[k + 1] === '*') continue
      if (isAlnum(input[k + 1])) continue
    }
    return k
  }
  return -1
}

/**
 * Split a string into bold / italic / text nodes. Only `**` (bold) and `*`
 * (italic) are treated as emphasis, and only with CommonMark-style flanking:
 *   - an opener must be immediately followed by a non-space (and, for single
 *     `*`, not immediately preceded by an alphanumeric);
 *   - a closer must be immediately preceded by a non-space (and, for single
 *     `*`, not immediately followed by an alphanumeric).
 * A span also needs BOTH a valid open and a valid close, so a lone `*`,
 * `2 * 3`, and `2*3` are all left as literal text. Inner content is parsed
 * recursively, so bold-wrapping-italic (and vice versa) works.
 */
export function parseRichText(input: string): RichNode[] {
  const nodes: RichNode[] = []
  let textStart = 0
  let i = 0

  while (i < input.length) {
    if (input[i] === '*') {
      const delim = input[i + 1] === '*' ? 2 : 1
      const afterOpen = input[i + delim]
      const beforeOpen = input[i - 1]
      const validOpen =
        !isSpace(afterOpen) && afterOpen !== '*' && (delim === 2 || !isAlnum(beforeOpen))

      if (validOpen) {
        const close = findCloser(input, i + delim, delim)
        if (close !== -1) {
          if (i > textStart) nodes.push({ type: 'text', text: input.slice(textStart, i) })
          const children = parseRichText(input.slice(i + delim, close))
          nodes.push(delim === 2 ? { type: 'bold', children } : { type: 'italic', children })
          i = close + delim
          textStart = i
          continue
        }
      }
    }
    i++
  }

  if (textStart < input.length) nodes.push({ type: 'text', text: input.slice(textStart) })
  return nodes
}

/** True if the text contains Markdown emphasis we'd render. */
export function hasEmphasis(text: string): boolean {
  return parseRichText(text).some((n) => n.type !== 'text')
}
